import React, { useEffect, useState } from "react"
import { useDispatch } from "react-redux"
import { useTranslation } from "react-i18next"
import { useForm } from "react-hook-form"
import { zodResolver } from "@hookform/resolvers/zod"
import { styled } from "@mui/material/styles"
import { kebabToCamelCase } from "utils/stringUtils"
import { PATH_INFOS } from "utils/navigatorUtils"
import { EDIT } from "constants/index"
import { MIN_LENGTH_ZOD_ERROR, SERVER_ERROR } from "constants/errors"
import { ResponseError } from "types/general.types"
import { useAppSelector } from "appRedux/hooks"
import { updateCurrentPageInfo, updateGlobalToastMessage } from "appRedux/slices/globalSlice"
import { useLazyGetUserProfileQuery, useUpdateUserProfileMutation } from "appRedux/slices/userApiSlice"
import { userProfileRequestSchema } from "validations/userProfile"
import CTA from "components/atoms/CTA"
import UserProfileFormFields from "components/organisms/UserProfileFormFields"

export default function EditUserProfileForm() {
  const [formFields, setFormFields] = useState<UserProfileUpdateRequestPayload | null>(null)
  const [avatarIsDirty, setAvatarIsDirty] = useState<boolean>(false)

  const currentPageInfo = useAppSelector((state) => state.global.currentPageInfo)

  const [updateUserInfo, result] = useUpdateUserProfileMutation()
  const [triggerGetUserProfile] = useLazyGetUserProfileQuery()

  const { t } = useTranslation()
  const dispatch = useDispatch()

  const {
    register,
    watch,
    reset,
    getValues,
    setValue,
    setError,
    handleSubmit,
    formState: { errors, dirtyFields, isDirty }
  } = useForm<UserProfileUpdateRequestPayload>({
    resolver: zodResolver(userProfileRequestSchema),
    shouldFocusError: false,
    reValidateMode: "onBlur",
    defaultValues: {
      name: "",
      phoneNumber: "",
      email: "",
      birthday: "",
      gender: "",
      avatar: ""
    }
  })

  useEffect(() => {
    if (!Object.keys(errors).length) return
    console.log(errors)
    handleErrors()
  }, [errors])

  useEffect(() => {
    if (currentPageInfo?.path !== PATH_INFOS.editUserProfile.path) return
    updateHeaderButton()
  }, [isDirty, result, currentPageInfo?.path, avatarIsDirty])

  useEffect(() => {
    if (result.isSuccess) {
      handleProfileUpdatedSuccessfully()
    }
    if (result.isError) {
      handleProfileUpdateFailed(result.error as ResponseError)
    }
  }, [result])

  function handleErrors() {
    if (errors.email) {
      if (errors.email.type === MIN_LENGTH_ZOD_ERROR) {
        dispatch(updateGlobalToastMessage({
          severity: "error",
          key: SERVER_ERROR,
          show: true,
          status: 500,
          message: "errorMessage.enterEmail"
        }))
      } else {
        dispatch(updateGlobalToastMessage({
          severity: "error",
          key: SERVER_ERROR,
          show: true,
          status: 500,
          message: `auth.invalidEmail`
        }))
      }
    }
  }

  function updateHeaderButton() {
    const headerRightButton = <div style={{ position: "absolute", right: 0 }}>
      <CTA
        variant="text"
        disabled={avatarIsDirty ? !avatarIsDirty : (!isDirty || result.isLoading)}
        loading={result.isLoading}
        onClick={handleSubmit(saveUserProfileInfo)}
      >
        {t("general.save")}
      </CTA>
    </div>

    dispatch(updateCurrentPageInfo({
      headerButtons: {
        right: [{ icon: headerRightButton, eventName: "", key: EDIT }],
        left: null
      }
    }))
  }

  function handleProfileUpdatedSuccessfully() {
    if (formFields) {
      reset(formFields)
    }
    triggerGetUserProfile()
    dispatch(updateGlobalToastMessage({
      severity: "success",
      key: "profileEditedSuccessfully",
      show: true,
      message: "successMessage.savedSuccessfully"
    }))
  }

  function handleProfileUpdateFailed(error: ResponseError) {
    if (!error.data) return
    const errorData: any = error.data
    const errorMessage = errorData.message || ""
    if (errorMessage.startsWith("Duplicate entry")) {
      handleDuplicateEntry(errorMessage)
    }
  }

  function handleDuplicateEntry(errorMessage: string) {
    const chunks = errorMessage.match(/(?:[^'"]+|"[^"]*")+/g) || []
    const errorField = chunks[chunks?.length - 1].split(".")[1]
    const camelizedErrorField = kebabToCamelCase(errorField)
    // @ts-ignore
    setError(camelizedErrorField, { type: "custom", message: "custom message" })
  }

  async function saveUserProfileInfo(value: UserProfileUpdateRequestPayload) {
    setFormFields(value)
    const formData = new FormData()
    try {
      const parsedValue = await userProfileRequestSchema.parseAsync(value)
      Object.entries(parsedValue).forEach(([key, val]) => {
        if (key === "avatar" && !dirtyFields.hasOwnProperty(key)) return
        formData.append(key, val)
      })
      updateUserInfo(formData)
      setAvatarIsDirty(false)
    } catch (err) {
      console.error(err)
    }
  }

  return (
    <Form onSubmit={handleSubmit(saveUserProfileInfo)}>
      <UserProfileFormFields
        register={register}
        watch={watch}
        reset={reset}
        getValues={getValues}
        setValue={setValue}
        errors={errors}
        setAvatarIsDirty={setAvatarIsDirty}
      />
    </Form>
  )
}

const Form = styled("form")(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  justifyContent: "space-between",
  flexGrow: 1
}))
